import { createMetricSender } from '@/src/metrics/send-metric';
import { getDeeplinkShopDetail, PICK_UP } from '@/src/utils/constants';
import shopClicked from '@/src/utils/tracking/shopClicked';
import shopListUpdated from '@/src/utils/tracking/shopListUpdated';
import shopListExpanded from '@/src/utils/tracking/shopListExpanded';
import filtersShown from '@/src/utils/tracking/filtersShown';
import filterClicked from '@/src/utils/tracking/filterClicked';
import { IFilter, ModalSorting } from '@components/ModalSorting/ModalSorting';
import SkeletonFilter from '@components/Skeleton/SkeletonFilter';
import { Space } from '@components/Space/Space';
import { VENDORS } from '@constants/vendors';
import useGetVendors from '@hooks/useGetVendors';
import InputDropDown from '@pedidosya/web-fenix/system/InputDropDown';
import InputDropDownOption from '@pedidosya/web-fenix/system/InputDropDownOption';
import Skeleton from '@pedidosya/web-fenix/system/Skeleton';
import ChevronDown from '@pedidosya/web-fenix/icons/ChevronDown';
import ChipCarouselS from '@pedidosya/web-fenix/system/ChipCarouselS';
import ChipS from '@pedidosya/web-fenix/system/ChipS';
import { useCallback, useEffect, useRef, useState } from 'react';
import STATUS_CODE from '@/src/constants/statusCode';
import { ErrorVendorList } from '../ErrorVendorList/ErrorVendorList';
import SkeletonVendorItemCard from '../Skeleton/SkeletonVendorItemCard';
import {
  ChipCarrouselContainer,
  ContainerChipCarouselS,
  ContainerDropdown,
  ContainerPartnerList,
  PartnerListContainer,
  SkeletonQuantity,
} from './PartnerList.styles';

import VendorListingContent from './components/VendorListingContent';
import { PartnerListProps } from './types';

const optionsDropdown = [
  { label: 'Sugeridos', value: 'RANKING' },
  { label: 'Más cercanos', value: 'DISTANCE' },
  { label: 'Más rápidos', value: 'DELIVERY_TIME' },
];

const PartnerList = ({
  origin,
  landingId,
  filters,
  country,
  lat,
  lng,
  landingType,
  landingTitle,
  channelId,
  currencyIsoCode,
  platform,
  showFilters = false,
  banner,
  isDesktop,
  headers,
  filtersMetadata,
  strategyId,
  rankingRequestId,
  isNewVersion = false,
  initialVendors,
  initialTotalVendors,
  initialSize,
  initialOriginalPeyaTrace,
  initialMidasRequestId,
  isMigration,
}: PartnerListProps & { isMigration: boolean }) => {
  const [hasErrorRetry, setHasErrorRetry] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [filterSelected, setFilterSelected] = useState(filters?.main);
  const [sorting, setSorting] = useState<IFilter>(undefined);
  const [sortingDesktop, setSortingDesktop] = useState(optionsDropdown[0]);
  const [lastVendorClicked, setLastVendorClicked] = useState(undefined);
  const [lastVendorClickedTimeStamp, setLastVendorClickedTimeStamp] =
    useState(undefined);
  const [showSkeletonFilter, setShowSkeletonFilter] = useState(false);
  const metricSender = createMetricSender();
  const [updateTrackingDone, setUpdateTrackingDone] = useState(false);
  const lastTrackedPage = useRef(1);

  const {
    loading,
    vendorError,
    vendors,
    totalVendors,
    setVendorError,
    setVendors,
    setTotalVendors,
    midasRequestId,
    page,
    setPage,
    setLoading,
    size,
    originalPeyaTrace,
  } = useGetVendors(
    landingId,
    country,
    lat,
    lng,
    landingType,
    filterSelected,
    sorting,
    filtersMetadata,
    headers,
    initialVendors,
    initialTotalVendors,
    initialSize,
    initialOriginalPeyaTrace,
    initialMidasRequestId,
    isMigration,
  );

  const is404Error = vendorError === STATUS_CODE.NOT_FOUND;
  const isFilterChanged = filterSelected !== filters?.main;
  const isFilterChange404 = is404Error && isFilterChanged;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeletonFilter(false);
    }, 1200);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (vendors?.length > 0 && page === 1 && !updateTrackingDone) {
      const vendorIds = vendors?.map((vendor) => vendor.id).join(',');
      const ncrTokensVendors = vendors
        .filter((vendor) => vendor?.ncr_token)
        .map((vendor) => vendor.ncr_token)
        .join(', ');
      const positionsGoldVipVendors = vendors?.reduce(
        (acc, vendor, index) => (vendor.ncr_token ? [...acc, index] : acc),
        [],
      );
      const vendorsQuantityGoldVip = vendors?.filter(
        (vendor) => !!vendor.ncr_token,
      )?.length;
      shopListUpdated(
        banner?.bannerId,
        banner?.bannerName,
        vendorIds,
        ncrTokensVendors,
        vendorsQuantityGoldVip,
        positionsGoldVipVendors,
        filterSelected,
        filterSelected,
        origin,
        totalVendors,
        sorting,
        originalPeyaTrace || rankingRequestId,
        landingId,
        landingTitle,
        landingType,
      );
      setUpdateTrackingDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, page, shopListUpdated, updateTrackingDone]);

  useEffect(() => {
    if (page === 1) {
      lastTrackedPage.current = 1;
    }

    if (vendors?.length > 0 && page >= 2 && lastTrackedPage.current !== page) {
      const vendorIds = vendors?.map((vendor) => vendor.id).join(',');
      const positionsGoldVipVendors = vendors?.reduce(
        (acc, vendor, index) => (vendor.ncr_token ? [...acc, index] : acc),
        [],
      );
      const vendorsQuantityGoldVip = vendors?.filter(
        (vendor) => !!vendor.ncr_token,
      )?.length;

      const selectedFilters = {
        specialTags: [],
        foodFilters: filterSelected ? [filterSelected.toLowerCase()] : [],
      };

      shopListExpanded(
        origin,
        page,
        size,
        positionsGoldVipVendors,
        vendorsQuantityGoldVip,
        filterSelected,
        vendors.length,
        totalVendors,
        vendorIds,
        sorting,
        selectedFilters,
      );
      lastTrackedPage.current = page;
    }
  }, [vendors, page, filterSelected, origin, size, totalVendors, sorting]);

  useEffect(() => {
    if (filters?.list?.length > 0) {
      const availableMainFilterNames = filters.list.map(
        (filter) => filter.name,
      );
      const availableSortOptionValues = optionsDropdown.map(
        (option) => option.value,
      );

      const allAvailableFiltersString = [
        ...availableMainFilterNames,
        ...availableSortOptionValues,
      ]
        .join(',')
        ?.toLowerCase();

      const activeFilterParts = [];

      if (filterSelected) {
        activeFilterParts.push(filterSelected);
      }

      if (sorting) {
        activeFilterParts.push(String(sorting));
        filterClicked(filterSelected, sorting, landingType);
      }

      const filtersSelectedString = activeFilterParts.join(',')?.toLowerCase();

      filtersShown(
        filterSelected,
        filtersSelectedString,
        allAvailableFiltersString,
        landingType,
      );
    }
  }, [filters, sorting, filterSelected, landingType]);

  const totalVendorsPerPage = parseInt(VENDORS.VENDORS_TOTAL_BY_PAGE, 10);

  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      const hasScrolled = typeof window !== 'undefined' && window.scrollY > 0;

      // Only trigger loading when we're not already loading and there's more content to fetch
      if (
        target.isIntersecting &&
        !loading &&
        !vendorError &&
        vendors?.length < totalVendors &&
        (!isMigration || page > 1 || hasScrolled)
      ) {
        // Set loading state before incrementing page to prevent multiple triggers
        setLoading(true);
        setPage((prevPage) => prevPage + 1);
      }

      // Handle error retries separately
      if (
        target.isIntersecting &&
        vendorError?.code === 'ECONNABORTED' &&
        hasErrorRetry < 3
      ) {
        setHasErrorRetry(hasErrorRetry + 1);
        setLoading(true);
      }

      if (hasErrorRetry === 3) {
        setVendorError(500);
      }
    },
    [
      vendorError,
      loading,
      vendors?.length,
      totalVendors,
      hasErrorRetry,
      setLoading,
      setPage,
      setVendorError,
      isMigration,
      page,
    ],
  );

  const loader = useRef(null);

  useEffect(() => {
    const options = {
      root: null,
      threshold: 0,
    };

    const observer = new IntersectionObserver(handleObserver, options);
    if (loader.current) observer.observe(loader.current);
    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  const redirectToHome = () => {
    document.location = `pedidosya://home`;
  };

  const setValuesDefault = async (vertical: string) => {
    setPage(1);
    setVendorError(undefined);
    setFilterSelected(vertical);
    setTotalVendors(null);
    setVendors([]);
    setLoading(true);
  };

  const getVendorRankType = (rankType: any): string => {
    if (rankType === 'FEATURED_CPC') return 'True: premium_placements__cpc';
    if (rankType === 'FEATURED_CPP') return 'True: premium_placements__cpp';
    return 'False';
  };

  const redirectToShopDetail = (id, occasion) => {
    let deeplinkToShopDetail = getDeeplinkShopDetail(id, occasion);
    if (
      [
        '2ed494d9-b343-4bbb-b243-deb12401f05e',
        '6f34aa60-a19e-4a78-9f7c-b89246c35256',
        'bb47575b-f940-4822-9317-906fc4d05eed',
        'd1019897-4aa5-470a-bbce-a4a23ef941ed',
        'aa53d598-1f50-4c6f-8a17-9db2a9d19912',
        '24585087-dbd6-474b-b8f1-f4da39747ecb',
        '12d18649-12d7-4a5f-8174-97ca58a95059',
      ].includes(landingId)
    ) {
      deeplinkToShopDetail += `&scrolling_product_tag=cheap`;
    }

    if (occasion === PICK_UP) {
      deeplinkToShopDetail += `&pickup=true`;
    }

    if (occasion === 'PICKUP') {
      deeplinkToShopDetail += `&pickup=true`;
      deeplinkToShopDetail = deeplinkToShopDetail.replace('PICKUP', 'PICK_UP');
    }

    if (channelId !== undefined && channelId?.length > 0) {
      deeplinkToShopDetail += `&channel_id=${channelId}`;
    }

    if (strategyId !== undefined && strategyId?.length > 0) {
      deeplinkToShopDetail += `&strategy_id=${strategyId}`;
    }

    if (filterSelected === 'COURIER') {
      document.location = 'pedidosya://verticals?bt=COURIER';
      return;
    }

    document.location = deeplinkToShopDetail;
  };

  const redirectToWeb = async (url) => {
    document.location = url;
  };

  const handleClickVendor = (vendor, vendorPosition, event) => {
    const MIN_DEFAULT_TIME_WAIT_VENDOR_CALL = 1500;
    setLastVendorClicked(vendor?.id);
    setLastVendorClickedTimeStamp(event.timeStamp);
    if (
      lastVendorClicked !== vendor?.id ||
      event.timeStamp - lastVendorClickedTimeStamp >=
        MIN_DEFAULT_TIME_WAIT_VENDOR_CALL
    ) {
      metricSender.onVendorClick();

      if (vendorPosition >= 0 && vendorPosition <= 9) {
        metricSender.onVendorPositionTopClick();
      }
      if (vendorPosition >= 10 && vendorPosition <= 24) {
        metricSender.onVendorTop25PositionClick();
      }
      if (vendorPosition >= 25 && vendorPosition <= 49) {
        metricSender.onVendorTop50PositionClick();
      }

      if (vendorPosition >= 50) {
        metricSender.onVendorHigh50PositionClick();
      }

      shopClicked({
        origin,
        shopGoldVip: String(vendor?.labels?.sponsoringLabel === 'Anuncio'),
        ncrRequestToken: vendor?.ncrToken,
        shopSponsoring: getVendorRankType(vendor?.rankType),
        midasRequestId,
        shopId: vendor?.id,
        currencyCode: currencyIsoCode,
        shopRatingQuality: vendor?.rating,
        shopName: vendor?.name,
        shopDeliveryFee: vendor?.delivery?.deliveryFee,
        shopDeliveryTime: vendor?.delivery?.deliveryTime,
        position: String(vendor.rank - 1),
        shopListBusinessType: filterSelected,
        shopDistance: vendor?.delivery?.distance,
        bannerId: banner?.bannerId,
        rankingRequestId: originalPeyaTrace || rankingRequestId,
        landingId,
        landingTitle,
        landingType,
      });
      redirectToShopDetail(vendor?.id, vendor?.occasion);
    }
  };

  const labelTotalVendors = () => {
    if (loading) {
      return (
        <SkeletonQuantity>
          <Skeleton
            height="4"
            shape="square"
            width="20"
            data-testid="skeleton-quantity"
          />
        </SkeletonQuantity>
      );
    }

    if (!totalVendors) return null;

    return null;
  };

  return (
    <>
      <ContainerPartnerList isDesktop={isDesktop}>
        {filters?.list?.length > 0 ? (
          !showSkeletonFilter ? (
            <ChipCarrouselContainer
              showFilters={showFilters && !isDesktop}
              isiOS={platform === 'ios'}
              isAndroid={platform === 'android'}
              isNewVersion={isNewVersion}
            >
              <ContainerChipCarouselS showFilters={showFilters && !isDesktop}>
                <ChipCarouselS
                  buttonLabel={(() => {
                    switch (sorting) {
                      case 'RANKING':
                        return 'Nuestros sugeridos';
                      case 'DISTANCE':
                        return 'Más cercanos';
                      case 'DELIVERY_TIME':
                        return 'Más rápidos';
                      default:
                        return 'Ordenar';
                    }
                  })()}
                  buttonIcon={<ChevronDown />}
                  onButtonClick={() => setIsOpen(true)}
                >
                  {filters?.list?.map((filter) => (
                    <ChipS
                      label={filter.title}
                      key={filter.name}
                      selected={filterSelected === filter.name}
                      onClick={() => {
                        filterClicked(filterSelected, filter.name, landingType);
                        setValuesDefault(filter.name);
                      }}
                    />
                  ))}
                </ChipCarouselS>
              </ContainerChipCarouselS>
            </ChipCarrouselContainer>
          ) : (
            <SkeletonFilter />
          )
        ) : null}

        {isDesktop && (
          <ContainerDropdown>
            <InputDropDown
              errorMessage="Error message"
              label={`Ordenar por: `}
              selectedOption={sortingDesktop}
            >
              <InputDropDownOption
                label={optionsDropdown[0].label}
                onClick={() => {
                  metricSender.onRankingFilterClick();
                  setSortingDesktop(optionsDropdown[0]);
                  setSorting(optionsDropdown[0].value);
                  setValuesDefault(filterSelected);
                }}
                selected={optionsDropdown[0].value === sorting}
                value={optionsDropdown[0].value}
              />
              <InputDropDownOption
                label={optionsDropdown[1].label}
                onClick={() => {
                  metricSender.onDistanceFilterClick();
                  setSortingDesktop(optionsDropdown[1]);
                  setSorting(optionsDropdown[1].value);
                  setValuesDefault(filterSelected);
                }}
                selected={optionsDropdown[1].value === sorting}
                value={optionsDropdown[1].value}
              />
              <InputDropDownOption
                label={optionsDropdown[2].label}
                onClick={() => {
                  metricSender.onDeliveryTimeFilterClick();
                  setSortingDesktop(optionsDropdown[2]);
                  setSorting(optionsDropdown[2].value);
                  setValuesDefault(filterSelected);
                }}
                selected={optionsDropdown[2].value === sorting}
                value={optionsDropdown[2].value}
              />
            </InputDropDown>
          </ContainerDropdown>
        )}
      </ContainerPartnerList>
      {!isDesktop && (
        <ModalSorting
          open={isOpen}
          onClose={() => setIsOpen(!isOpen)}
          onClickHandler={() => {
            setValuesDefault(filterSelected);
          }}
          filter={sorting}
          setFilter={setSorting}
        />
      )}
      <PartnerListContainer
        isDesktop={isDesktop}
        showFilters={showFilters}
        isAndroid={platform === 'android'}
        isNewVersion={isNewVersion}
      >
        {labelTotalVendors()}
        <VendorListingContent
          vendors={vendors}
          isDesktop={isDesktop}
          totalVendors={totalVendors}
          handleClickVendor={handleClickVendor}
          redirectToWeb={redirectToWeb}
          filterSelected={filterSelected}
          origin={origin}
          rankingRequestId={originalPeyaTrace || rankingRequestId}
          landingId={landingId}
          landingTitle={landingTitle}
          landingType={landingType}
        />

        {vendors?.length <= totalVendorsPerPage &&
          vendors?.length === totalVendors && <Space mt={8} />}
        {vendorError && (
          <ErrorVendorList
            error={vendorError}
            onAction={() => redirectToHome()}
            location={country}
            isFilterError={isFilterChange404}
          />
        )}

        {loading && !vendorError && totalVendors !== vendors?.length ? (
          <SkeletonVendorItemCard isDesktop={isDesktop} count={9} />
        ) : null}
        <div id="loader_more_vendors" ref={loader} />
      </PartnerListContainer>
    </>
  );
};

export default PartnerList;
